import React from "react";
import {
  Page,
  ChapterProps as RPDFCChapterProps,
  Chapter as RPDFCChapter,
  Image as RPDFCImage,
  View as RPDFCView
} from "@surge-global-engineering/react-pdf-components";
import { View as RPDFView } from "@surge-global-engineering/rpdf-renderer";
import { getNormalizedOpacity } from "@surge-global-engineering/css-generator";
import { arabToRoman } from "roman-numbers";
import {
  PdfChapter,
  PdfExpandedBook,
  PdfTheme,
  PdfExtendedTOCSummaryItem,
  PdfSlateBaseParentBlockNode,
  PdfSlateEndnote,
} from "../types";
import { ADDITIONAL_FOOTER_HEIGHT, computeHeaderPageNumberContainerWidth, getThemeConfigsForChapterTypes, HEADER_MIN_HEIGHT, SPACE_BETWEEN_FOOTER_AND_BODY } from "../configs";
import {
  centimetersToPt,
  mapHeaderFooterFontSize,
  getLayoutChapterProperties,
  prepareThemeConfigForChapterType,
  getAdjustedBookDimensions,
  prepareThemeConfigForFontFamily,
  withoutUnit
} from "../helpers";
import ChapterBody from "./ChapterBody";
import BookTitleCard from "./BookTitleCard";
import ChapterTitleCard from "./ChapterTitleCard";
import TOC from "./TOC";
import { getBookEndnotes, getBookEndnotesByChapter } from "../../../../utils/get-book-endnotes";
import { HeaderImage } from "@surge-global-engineering/css-generator";
import { Font } from "@surge-global-engineering/rpdf-renderer";
import { filterTrackChanges } from "../../../../utils/track-changes";

export interface ChapterProps {
  book: PdfExpandedBook;
  chapters: PdfChapter[];
  chapter: PdfChapter;
  theme: PdfTheme;
  chapterNumber?: number;
  endNoteOffset: number;
  /**
   * Only required by the TOC chapter type
   */
  tocSummary?: PdfExtendedTOCSummaryItem[];
  assumeUsingOnlyFirstPage?: boolean;
  firstPageLightText?: boolean;
  isSinglePageCopyright?: boolean; // used only in copyright
}

const Chapter: React.FC<ChapterProps> = ({
  book,
  chapter,
  theme,
  chapterNumber,
  endNoteOffset,
  tocSummary,
  assumeUsingOnlyFirstPage = false,
  firstPageLightText = false,
  isSinglePageCopyright = false
}) => {
  const { type: chapterType, children, volume, title, image: individualChapterImageSrc, _id: chapterId, configuration } = chapter;
  const { showChapterImage = true, showChapterHeading = true, showHeaderFooter = true, showPageNumber = true } = configuration || {};
  const { title: bookTitle, author: bookAuthor } = book;
  // Validate `theme` according to the chapter type
  let _themeConfig = prepareThemeConfigForChapterType(
    theme,
    chapterType
  );

  // filter out the track-changes from the chapter body
  const filteredChapterContent = filterTrackChanges(children);

  // transform `theme baseFontSize` according to the baseFont
  _themeConfig = prepareThemeConfigForFontFamily(_themeConfig);
  const { layout, printBaseFont } = _themeConfig.properties;

  const { height, width, marginOutside, paddingBottom, paddingTop, marginInside } = getAdjustedBookDimensions(_themeConfig, book);

  const defaultLayoutChapterProperties = getLayoutChapterProperties(
    layout,
    bookAuthor.join(", ")?.toUpperCase(),
    bookTitle?.toUpperCase(),
    title?.toUpperCase()
  );

  const getFooterStyles = () => {
    return {
      fontSize: mapHeaderFooterFontSize(theme?.properties?.footer?.size, _themeConfig.properties.baseFontSize),
      fontFamily: theme?.properties?.footer?.font || _themeConfig.properties.printBaseFont,
      lineHeight: 1
    };
  };

  const getHeaderStyles = () => {
    return {
      fontSize: mapHeaderFooterFontSize(theme?.properties?.header.size, _themeConfig.properties.baseFontSize),
      fontFamily: theme?.properties?.header?.font || _themeConfig.properties.printBaseFont,
    };
  };

  const getHeaderPageNumberContainerWidth = () => {
    // the font size is in PT
    const { fontSize, fontFamily } = getHeaderStyles();
    const fontSizeWithoutUnit = typeof fontSize === "number" ? fontSize : withoutUnit(fontSize, "pt");
    return computeHeaderPageNumberContainerWidth(fontSizeWithoutUnit, fontFamily);
  };
  
  // Reload font variants for the current fontFamily to support italic and bold formatting for footnotes.
  if (theme.properties.notesMode === "FOOTNOTE") {
    const baseFont = theme.properties.printBaseFont;
    Font.load({ fontFamily: baseFont, fontWeight: 400 });
    Font.load({ fontFamily: baseFont, fontWeight: 900, fontStyle: "italic" });
    Font.load({ fontFamily: baseFont, fontWeight: 900 });
    Font.load({ fontFamily: baseFont, fontStyle: "italic" }); 

   //load fonts variants for monospace
    Font.load({ fontFamily: "CourierPrime", fontWeight: 900, fontStyle: "italic" });
    Font.load({ fontFamily: "CourierPrime", fontWeight: 900 });
    Font.load({ fontFamily: "CourierPrime", fontStyle: "italic" });
   //load font variants for sans seriff
    Font.load({ fontFamily: "PTSans", fontWeight: 900, fontStyle: "italic" });
    Font.load({ fontFamily: "PTSans", fontWeight: 900 });
    Font.load({ fontFamily: "PTSans", fontStyle: "italic" });
}

  const isEndnoteChapter = () => {
    return chapter.type === "endnotes";
  };
  /**
   * For End of Book endnotes
   */
  const everyEndNoteInBook =
    theme.properties.notesMode === "END_OF_BOOK" && isEndnoteChapter() && !theme.properties.pdfEndnotesChapterSettings.categorizeByTitle
      ? getBookEndnotes(book.chapters) : theme.properties.pdfEndnotesChapterSettings.categorizeByTitle && theme.properties.notesMode === "END_OF_BOOK" && isEndnoteChapter() ? getBookEndnotesByChapter(book.chapters) 
      : undefined;

  const getFooterMinHeight = () => {
    const { pageNumberPosition } = defaultLayoutChapterProperties;
    if (pageNumberPosition !== "bottom") { return 0; }
    const fontSize = withoutUnit(mapHeaderFooterFontSize(theme?.properties?.header.size, _themeConfig.properties.baseFontSize).toString(), "pt");
    return fontSize + ADDITIONAL_FOOTER_HEIGHT;
  };

  const getSpaceBetweenBodyAndFooter = () => {
    const { pageNumberPosition } = defaultLayoutChapterProperties;
    if (pageNumberPosition !== "bottom") { return 0; }
    return SPACE_BETWEEN_FOOTER_AND_BODY;
  };

  const defaultChapterProps: RPDFCChapterProps = {
    paddingBottom: centimetersToPt(paddingBottom),
    paddingTop: centimetersToPt(paddingTop),
    marginInside: centimetersToPt(marginInside),
    marginOutside: centimetersToPt(marginOutside),
    pageHeight: centimetersToPt(height),
    pageWidth: centimetersToPt(width),
    pageNumberContainerWidth: getHeaderPageNumberContainerWidth(),

    getTransformedPageNumber: (pageNumber) => {
      if (pageNumber === 1) return "";
      return pageNumber.toString();
    },
    ...defaultLayoutChapterProperties,

    footerStyles: { ...getFooterStyles(), minHeight: getFooterMinHeight() },
    headerStyles: { ...getHeaderStyles(), minHeight: HEADER_MIN_HEIGHT },
    headerHiddenPages: [1],
    spaceBetweenBodyAndFooter: getSpaceBetweenBodyAndFooter()
  };

  if (chapterType === "title") {
    const { marginInside, marginOutside, paddingBottom, paddingTop } = defaultChapterProps;
    return (
      <Page
        size={{
          width: centimetersToPt(width),
          height: centimetersToPt(height),
        }}
        style={{ fontFamily: printBaseFont }}
      >
        <RPDFCView style={{ marginOutside, marginInside, paddingBottom, paddingTop, height: "100%" }}>
          <BookTitleCard
            key="book-title-card"
            book={book}
            chapter={chapter}
            theme={_themeConfig}
          ></BookTitleCard>
        </RPDFCView>
      </Page>
    );
  }

  if (chapterType === "copyrights") {
    const { baseFontSize } = _themeConfig.properties;
    const copyrightsContentHeight = centimetersToPt(height - (paddingBottom + paddingTop)) - (SPACE_BETWEEN_FOOTER_AND_BODY + getFooterMinHeight() + 30);
    return (
      <RPDFCChapter
        {...defaultChapterProps}
        getTransformedPageNumber={() => ""}
        style={{ fontFamily: printBaseFont }}
      >
        <RPDFView
          style={{
            ...(isSinglePageCopyright && {
              height: copyrightsContentHeight,
              justifyContent: "flex-end",
            }),
          }}
        >
          <ChapterBody
            endNoteOffset={endNoteOffset}
            theme={{
              ..._themeConfig,
              properties: {
                ..._themeConfig.properties,
                baseFontSize: baseFontSize * 0.75,
              },
            }}
            chapterLevelConfig={chapter.configuration || {}}
          >
            {filteredChapterContent}
          </ChapterBody>
        </RPDFView>
      </RPDFCChapter>
    );
  }

  if (chapterType === "toc") {
    return (
      <TOC
        chapter={chapter}
        book={book}
        tocSummary={tocSummary || []}
        theme={_themeConfig}
        chapterProps={defaultChapterProps}
      />
    );
  }

  if (!["toc", "title", "image", "copyrights"].includes(chapterType)) {
    const { titleCard } = _themeConfig.properties;
    const { image: isTitleImageVisible } = titleCard;
    const themeConfigsForChapterTypes =
      getThemeConfigsForChapterTypes(chapterType);

    const handlePageNumberTransformation = (pageNumber: number) => {
      if (themeConfigsForChapterTypes.footerVisibility === "hide" || !showHeaderFooter || !showPageNumber) return "";
      if (book.frontMatterIds.includes(chapterId)) {
        return (arabToRoman(pageNumber) || " ").toLowerCase();
      }
      return pageNumber.toString();
    };

    const getFormattedLayoutChapterProperties = () => {
      const author = volume?.author
        ? volume.author
        : bookAuthor.join(", ")?.toUpperCase(); // TODO: Revisit this piece after the completion of Volumes (since book author formatting allows to have multiple author names)
      const layoutData = getLayoutChapterProperties(
        layout,
        author,
        bookTitle?.toUpperCase(),
        title?.toUpperCase()
      );

      const { headerVisibility } = themeConfigsForChapterTypes;
      return {
        ...layoutData,
        evenPageHeaderText:
          (headerVisibility === "hide" || chapterType === "endnotes" || !showHeaderFooter) ? "" : layoutData.evenPageHeaderText,
        oddPageHeaderText:
          (headerVisibility === "hide" || chapterType === "endnotes" || !showHeaderFooter) ? "" : layoutData.oddPageHeaderText,
      };
    };

    const getBackgroundImageSrcValue = (): Pick<RPDFCChapterProps, "backgroundImageSrc" | "backgroundImageStyles"> | null => {
      let _image: HeaderImage | null = null;
      if (!isTitleImageVisible || !showChapterImage) return null;

      _image = theme.properties.image;
      if (theme.properties.individualChapterImage) {
        _image.url = individualChapterImageSrc;
      }

      if (!_image) return null;

      const { printExtent, placement, url, colored, opacity } = _image;
      // TODO: Implement colored or grayscale image

      const _opacity = getNormalizedOpacity(opacity);

      if (chapterType === "endnotes") return null;
      if (placement !== "background-image") return null;
      if (!url) return null;

      if (printExtent !== "full-bleed") return null;

      return { backgroundImageSrc: url, backgroundImageStyles: { opacity: _opacity } };
    };

    const backgroundImage = getBackgroundImageSrcValue();

    const chapterProps: RPDFCChapterProps = {
      ...defaultChapterProps,
      ...getFormattedLayoutChapterProperties(),
      getTransformedPageNumber: handlePageNumberTransformation,
      ...backgroundImage,
      assumeUsingOnlyFirstPage,
      firstPageLightText: ["chapter", "custom"].includes(chapterType) ? firstPageLightText : false
    };

    return (
      <RPDFCChapter {...chapterProps} style={{ fontFamily: printBaseFont }}>
        {chapterType !== "custom" &&
          chapterType !== "endnotes" &&
          showChapterHeading && (
            <ChapterTitleCard
              chapter={chapter}
              theme={_themeConfig}
              chapterNumber={chapterNumber}
              key={"chapter-title-card"}
            />
          )}
        <ChapterBody
          endNoteOffset={endNoteOffset}
          allEndNotes={everyEndNoteInBook}
          theme={_themeConfig}
          lightText={assumeUsingOnlyFirstPage && firstPageLightText}
          chapterLevelConfig={chapter.configuration || {}}
          chapter={chapter}
        >
          {filteredChapterContent}
        </ChapterBody>
      </RPDFCChapter>
    );
  }

  if (chapterType === "image") {
    const { fullpageImage } = chapter;

    if (!fullpageImage)
      return (
        <Page
          size={{
            width: centimetersToPt(width),
            height: centimetersToPt(height),
          }}
        ></Page>
      );

    const { imageUrl, printExtent, verticalAlignment } = fullpageImage;
    const isFullBleed = printExtent === "full-bleed";

    const getJustifyContentValue = () => {
      switch (verticalAlignment) {
        case "bottom":
          return "flex-end";
        case "top":
          return "flex-start";
        case "center":
          return "center";
        default:
          return undefined;
      }
    };

    const chapterProps: RPDFCChapterProps = {
      ...defaultChapterProps,
      footerHiddenPages: [1],
      headerHiddenPages: [1],
      getTransformedPageNumber: () => "",
      ...(isFullBleed && imageUrl && { backgroundImageSrc: imageUrl }),
      assumeUsingOnlyFirstPage,
    };

    return (
      <RPDFCChapter {...chapterProps} style={{ fontFamily: printBaseFont }}>
        {!isFullBleed && (
          <RPDFView
            style={{
              height: "85vh",
              position: "absolute",
              top: 0,
              justifyContent: getJustifyContentValue(),
            }}
          >
            {!!imageUrl && (
              <RPDFCImage
                src={imageUrl}
                imageContainerStyle={{ width: "100%" }}
              />
            )}
          </RPDFView>
        )}
      </RPDFCChapter>
    );
  }

  return (
    <Page
      size={{
        width: centimetersToPt(width),
        height: centimetersToPt(height),
      }}
      style={{ fontFamily: printBaseFont }}
    >
      <ChapterBody
        endNoteOffset={endNoteOffset}
        allEndNotes={everyEndNoteInBook}
        theme={_themeConfig}
        chapterLevelConfig={chapter.configuration || {}}
      >
        {filteredChapterContent}
      </ChapterBody>
    </Page>
  );
};

export default Chapter;
